import { AppBar, Box, Toolbar } from '@mui/material';
import { useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import IconButton from '../components/ui/IconButton';
import { localStorageKeys, systemConfigKeyEnum } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { useSystemConfig } from '../hooks/useSystemConfig';
import { useTheme } from '../hooks/useTheme';
import useWebPushNotifications from '../hooks/useWebPushNotifications';
import ConfigurationAdminPage from '../pages/admin/ConfigurationAdminPage';
import ExternalAdminPage from '../pages/admin/ExternalAdminPage';
import InternalAdminPage from '../pages/admin/InternalAdminPage';
import MaterialAdminPage from '../pages/admin/MaterialAdminPage';
import VehiclesAdminPage from '../pages/admin/VehiclesAdminPage';
import Login from '../pages/auth/Login';
import Dashboard from '../pages/Dashboard';
import NotFoundPage from '../pages/NotFoundPage';
import OnLoadPage from '../pages/OnLoadPage';
import ExpensesPage from '../pages/operations/ExpensesPage';
import GnrPage from '../pages/operations/GnrPage';
import NeedsPage from '../pages/operations/NeedsPage';
import OperationsPage from '../pages/operations/OperationsPage';
import PlanningPage from '../pages/operations/PlanningPage';
import ReportingPage from '../pages/operations/ReportingPage';
import SiteValidationPage from '../pages/operations/SiteValidationPage';
import SummaryPage from '../pages/operations/SummaryPage';
import SupervisionPage from '../pages/operations/SupervisionPage';
import ValidationPage from '../pages/operations/ValidationPage';
import ProfilePage from '../pages/ProfilePage';
import FormEditPage from '../pages/qhse/FormEditPage';
import FormHistoryPage from '../pages/qhse/FormHistoryPage';
import FormsPage from '../pages/qhse/FormsPage';
import FormsUsersAccessPage from '../pages/qhse/FormsUsersAccessPage';
import PLIPage from '../pages/sales/PLIPage';
import { getTodayPeriod } from '../utils/dates';
import NavBar from './NavBar';
import ProfileMenu from './ProfileMenu';
import ProtectedRoute from './ProtectedRoute';
import SettingsPanel from './SettingsPanel';

const RedirectToLogin = () => {
    const location = useLocation();
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
};

const Routers = () => {
    const { authIsLoading, isAuthenticated, user } = useAuth();
    const { muiTheme } = useTheme();
    const { isLoading, getConfigValue } = useSystemConfig();
    useWebPushNotifications();
    const [expanded, setExpanded] = useState(() => {
        const storedExpanded = localStorage.getItem(localStorageKeys.SIDEBAR);
        return storedExpanded === null ? true : storedExpanded === 'true';
    });

    const [settingsOpen, setSettingsOpen] = useState(false);
    const toggleSettings = () => setSettingsOpen((prev) => !prev);

    const toggleSidebar = () => {
        setExpanded((prev) => {
            localStorage.setItem(localStorageKeys.SIDEBAR, String(!prev));
            return !prev;
        });
    };

    const getLogoByPeriod = () => {
        const period = getTodayPeriod(new Date());
        if (period === 'normal') return require('../assets/images/logos/my-sltp-transp.png');
        if (period === 'halloween') return require('../assets/images/logos/my-sltp-transp-halloween.png');
        if (period === 'christmas') return require('../assets/images/logos/my-sltp-transp-christmas.png');
        if (period === 'newYear') return require('../assets/images/logos/my-sltp-transp-newyear.png');
        return require('../assets/images/logos/my-sltp-transp.png');
    };

    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {!authIsLoading && !isLoading && isAuthenticated && (
                    <AppBar sx={{ backgroundColor: '#000', border: 0 }} variant="outlined" position="sticky" elevation={0}>
                        <Toolbar disableGutters sx={{ pl: 1.25, pr: 1.25 }}>
                            <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <IconButton iconName="menu" sx={{ color: '#fff' }} iconProps={{ fontSize: 'large' }} onClick={toggleSidebar} />
                                    <img style={{ height: 30 }} src={getLogoByPeriod()} alt="MY SLTP" title={'My SLTP'} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <IconButton
                                        iconName="settings"
                                        sx={{ color: '#fff' }}
                                        iconProps={{ fontSize: 'large' }}
                                        onClick={toggleSettings}
                                    />
                                    <ProfileMenu />
                                </Box>
                            </Box>
                        </Toolbar>
                    </AppBar>
                )}

                <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                    {!authIsLoading && !isLoading && isAuthenticated && <NavBar expanded={expanded} toggleSidebar={toggleSidebar} />}

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Box
                            component="main"
                            sx={{
                                backgroundColor: muiTheme.palette.background.default,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                borderRadius: { xs: 0, md: 2 },
                                mr: { xs: 0, md: 1.25 },
                                mb: { xs: 0, md: 1.25 },
                                minWidth: 0,
                                overflow: 'hidden',
                            }}
                        >
                            <Routes>
                                {authIsLoading || isLoading ? (
                                    <Route path="*" element={<OnLoadPage />} />
                                ) : isAuthenticated ? (
                                    <>
                                        <Route
                                            path="/"
                                            element={
                                                <ProtectedRoute requiredLevel="CONDUC" redirectTo="/operations/planning">
                                                    <Dashboard />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/operations/reporting"
                                            element={
                                                <ProtectedRoute requiredLevel="SUPERVISION">
                                                    <ReportingPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/operations"
                                            element={
                                                <ProtectedRoute requiredLevel="CONDUC">
                                                    <OperationsPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/operations/needs"
                                            element={
                                                <ProtectedRoute requiredLevel="CONDUC">
                                                    <NeedsPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route path="/profile" element={<ProfilePage />} />
                                        <Route path="/operations/planning" element={<PlanningPage />} />
                                        <Route
                                            path="/operations/validation"
                                            element={
                                                <ProtectedRoute requiredLevel="CONDUC">
                                                    <ValidationPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/operations/validation/site/:siteId"
                                            element={
                                                <ProtectedRoute requiredLevel="CONDUC">
                                                    <SiteValidationPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/operations/supervision"
                                            element={
                                                <ProtectedRoute requiredLevel="CONDUC">
                                                    <SupervisionPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/operations/summary"
                                            element={
                                                <ProtectedRoute requiredLevel="CONDUC">
                                                    <SummaryPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/operations/expenses"
                                            element={
                                                <ProtectedRoute requiredLevel="CONDUC">
                                                    <ExpensesPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/operations/gnr"
                                            element={
                                                <ProtectedRoute requiredLevel="PLANNING">
                                                    <GnrPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/qhse/forms"
                                            element={
                                                <ProtectedRoute requiredLevel="OBSERVERS">
                                                    <FormsPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/qhse/forms/users-access"
                                            element={
                                                <ProtectedRoute requiredLevel="OBSERVERS">
                                                    <FormsUsersAccessPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/qhse/forms/group/:groupId"
                                            element={
                                                <ProtectedRoute requiredLevel="OBSERVERS">
                                                    <FormHistoryPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/qhse/forms/:formId"
                                            element={
                                                <ProtectedRoute requiredLevel="OBSERVERS">
                                                    <FormEditPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/configuration"
                                            element={
                                                <ProtectedRoute requiredLevel="ADMIN">
                                                    <ConfigurationAdminPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/material"
                                            element={
                                                <ProtectedRoute requiredLevel="PLANNING">
                                                    <MaterialAdminPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/internal"
                                            element={
                                                <ProtectedRoute requiredLevel="PLANNING">
                                                    <InternalAdminPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/vehicles"
                                            element={
                                                <ProtectedRoute requiredLevel="PLANNING">
                                                    <VehiclesAdminPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/external"
                                            element={
                                                <ProtectedRoute requiredLevel="PLANNING">
                                                    <ExternalAdminPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/sales/pli"
                                            element={
                                                <ProtectedRoute requiredLevel="ETUDES">
                                                    <PLIPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route path="/login" element={<Navigate to="/" replace />} />
                                        <Route path="*" element={<NotFoundPage />} />
                                    </>
                                ) : (
                                    <>
                                        <Route path="/login" element={<Login />} />
                                        <Route path="*" element={<RedirectToLogin />} />
                                    </>
                                )}
                            </Routes>
                        </Box>

                        {/* Banners environnement */}
                        {process.env.REACT_APP_ENV === 'test' && (
                            <Box
                                sx={{
                                    backgroundColor: 'error.main',
                                    color: 'white',
                                    py: 0.25,
                                    borderRadius: '10px 10px 0 0',
                                    fontSize: 12,
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    mr: 1.25,
                                }}
                            >
                                WEB VERSION {process.env.REACT_APP_ENV} || BACK VERSION {getConfigValue(systemConfigKeyEnum.ENVIRONMENT)} : Merci de
                                ne rien toucher et d'accéder à <a href="https://my.sltp.eu">my.sltp.eu</a>
                            </Box>
                        )}
                        {process.env.REACT_APP_ENV === 'development' && (
                            <Box
                                sx={{
                                    backgroundColor: 'cyan',
                                    color: 'black',
                                    py: 0.25,
                                    borderRadius: '10px 10px 0 0',
                                    fontSize: 12,
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    mr: 1.25,
                                }}
                            >
                                WEB VERSION {process.env.REACT_APP_ENV} || BACK VERSION {getConfigValue(systemConfigKeyEnum.ENVIRONMENT)}
                            </Box>
                        )}
                    </Box>

                    {!authIsLoading && !isLoading && isAuthenticated && <SettingsPanel open={settingsOpen} onClose={toggleSettings} />}
                </Box>
            </Box>
        </Router>
    );
};

export default Routers;
